import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../context/NotificationContext";
import { useAdminUi } from "../../hooks/useAdminUi";
import { getNotificationNavigationPath } from "../../utils/notificationRoutes";
import { primeNotificationSound } from "../../utils/notificationAlert";

export default function AdminNotificationMenu() {
  const { ui } = useAdminUi();
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    permission,
    fcmStatus,
    fcmError,
    hasVapidKey,
    formatTimeAgo,
    markAsRead,
    markAllRead,
    clearAll,
    enablePush,
    bellBlinking,
    clearBellAlert,
  } = useNotifications();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [enabling, setEnabling] = React.useState(false);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    primeNotificationSound();
    setAnchorEl(e.currentTarget);
    clearBellAlert();
  };

  const browserPermission =
    typeof Notification !== "undefined" ? Notification.permission : permission;

  const handleClose = () => setAnchorEl(null);

  const handleEnable = async () => {
    setEnabling(true);
    try {
      await enablePush();
    } finally {
      setEnabling(false);
    }
  };

  const handleItemClick = (item) => {
    markAsRead(item.id);
    const path = getNotificationNavigationPath(item);
    handleClose();
    if (!path) return;
    if (path.startsWith("http")) {
      window.open(path, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(path);
  };

  const showEnable =
    browserPermission !== "granted" &&
    typeof Notification !== "undefined" &&
    fcmStatus !== "unsupported";

  return (
    <>
      <IconButton size="small" onClick={handleOpen} aria-label="Notifications">
        <Box
          sx={{
            position: "relative",
            display: "inline-flex",
            "@keyframes bellBlink": {
              "0%, 100%": { opacity: 1, transform: "scale(1)", color: ui.inkSoft },
              "50%": { opacity: 0.25, transform: "scale(1.15)", color: ui.gold || "#d4a84a" },
            },
            animation: bellBlinking ? "bellBlink 0.55s ease-in-out infinite" : "none",
          }}
        >
          <NotificationsNoneOutlinedIcon sx={{ fontSize: 22 }} />
          {unreadCount > 0 ? (
            <Box
              sx={{
                position: "absolute",
                top: -2,
                right: -2,
                minWidth: 16,
                height: 16,
                px: 0.4,
                borderRadius: 10,
                bgcolor: "#e53935",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                display: "grid",
                placeItems: "center",
                lineHeight: 1,
                animation: bellBlinking ? "bellBlink 0.55s ease-in-out infinite" : "none",
                boxShadow: bellBlinking ? "0 0 8px rgba(229, 57, 53, 0.8)" : "none",
              }}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Box>
          ) : null}
        </Box>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "min(360px, 92vw)", sm: 380 },
              maxHeight: 440,
              borderRadius: 2,
              border: `1px solid ${ui.borderLight}`,
              boxShadow: ui.shadowMd || 4,
              overflow: "hidden",
            },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.25, bgcolor: ui.panelMuted, borderBottom: `1px solid ${ui.borderLight}` }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: ui.ink }}>Notifications</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {unreadCount > 0 ? (
              <Button size="small" onClick={markAllRead} sx={{ textTransform: "none", fontSize: 12, minWidth: 0 }}>
                Mark all read
              </Button>
            ) : null}
            <IconButton size="small" onClick={handleClose} aria-label="Close notifications">
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {showEnable ? (
          <Box sx={{ px: 2, py: 1.5, bgcolor: ui.surface, borderBottom: `1px solid ${ui.borderLight}` }}>
            <Typography sx={{ fontSize: 12, color: ui.inkMuted, mb: 1 }}>
              {hasVapidKey
                ? "Enable browser push to receive real-time alerts."
                : "Firebase VAPID key is missing — add it to .env first."}
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={handleEnable}
              disabled={enabling || !hasVapidKey}
              sx={{ textTransform: "none" }}
            >
              {enabling ? <CircularProgress size={16} color="inherit" /> : "Enable notifications"}
            </Button>
            {fcmError ? (
              <Typography sx={{ fontSize: 11, color: "#c62828", mt: 1, lineHeight: 1.45 }}>{fcmError}</Typography>
            ) : null}
            {browserPermission === "denied" ? (
              <Typography sx={{ fontSize: 11, color: "#c62828", mt: 1, lineHeight: 1.45 }}>
                Notifications are blocked. Click the lock icon in the address bar → Site settings → Allow notifications.
              </Typography>
            ) : null}
            {!hasVapidKey ? (
              <Typography sx={{ fontSize: 11, color: ui.inkSoft, mt: 1, lineHeight: 1.45 }}>
                1. Firebase Console → Cloud Messaging → Web Push → copy key pair
                <br />
                2. Create <code>.env</code> in project root
                <br />
                3. Restart <code>npm run start</code>
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 36, color: ui.inkSoft, mb: 1 }} />
            <Typography sx={{ fontSize: 13, color: ui.inkMuted }}>No notifications yet</Typography>
            <Typography sx={{ fontSize: 12, color: ui.inkSoft, mt: 0.5 }}>
              New alerts will appear here when they arrive.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 300, overflowY: "auto" }}>
            {notifications.map((item, index) => {
              const path = getNotificationNavigationPath(item);
              return (
              <React.Fragment key={item.id}>
                {index > 0 ? <Divider component="li" /> : null}
                <ListItemButton
                  onClick={() => handleItemClick(item)}
                  sx={{
                    alignItems: "flex-start",
                    py: 1.25,
                    bgcolor: item.read ? "transparent" : ui.panelMuted,
                    cursor: path ? "pointer" : "default",
                    "&:hover": path ? { bgcolor: ui.rowHover || ui.panelMuted } : undefined,
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography sx={{ fontSize: 13, fontWeight: item.read ? 500 : 700, color: ui.ink }}>
                          {item.title}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: ui.inkSoft, flexShrink: 0 }}>
                          {formatTimeAgo(item.createdAt)}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Typography sx={{ fontSize: 12, color: ui.inkMuted, mt: 0.35, lineHeight: 1.45 }}>
                        {item.body || "—"}
                      </Typography>
                    }
                  />
                  {path ? (
                    <ChevronRightOutlinedIcon sx={{ fontSize: 18, color: ui.inkSoft, mt: 0.5, flexShrink: 0 }} />
                  ) : null}
                </ListItemButton>
              </React.Fragment>
              );
            })}
          </List>
        )}

        {notifications.length > 0 ? (
          <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${ui.borderLight}`, textAlign: "center" }}>
            <Button size="small" color="inherit" onClick={clearAll} sx={{ textTransform: "none", fontSize: 12 }}>
              Clear all
            </Button>
          </Box>
        ) : null}
      </Popover>
    </>
  );
}
